import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { IconButton } from './IconButton'
import { CloseIcon } from './icons'

export interface OverlayProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  children: ReactNode
  /** Buttons pinned below the scroll area. */
  footer?: ReactNode
  /** Backdrop tap and Escape close it. Off for destructive confirmations. */
  dismissible?: boolean
  /** Hide the close button in the header. */
  hideClose?: boolean
  className?: string
  labelledBy?: string
  ariaLabel?: string
}

interface Props extends OverlayProps {
  kind: 'sheet' | 'dialog'
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

/** Shared modal machinery for Sheet and Dialog: portal, escape, scroll lock, focus return. */
export function Overlay({
  kind,
  open,
  onClose,
  title,
  children,
  footer,
  dismissible = true,
  hideClose = false,
  className,
  labelledBy,
  ariaLabel,
}: Props) {
  const panel = useRef<HTMLDivElement | null>(null)
  const restoreTo = useRef<Element | null>(null)

  useEffect(() => {
    if (!open || typeof document === 'undefined') return
    restoreTo.current = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab' || !panel.current) return
      const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) return
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    // Focus the panel itself rather than the first control: on a phone, auto-focusing
    // an input yanks the keyboard up before the user has read anything. A child that
    // asked for focus (`autoFocus` on a search box) has it already, and keeps it: this
    // effect runs after the children mount, so focusing the panel would take it away.
    if (!panel.current?.contains(document.activeElement)) panel.current?.focus()

    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = previousOverflow
      const back = restoreTo.current
      if (back instanceof HTMLElement) back.focus()
    }
  }, [open, dismissible, onClose])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className={`rs-scrim ${kind === 'sheet' ? 'rs-scrim--bottom' : 'rs-scrim--centre'}`}
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panel}
        className={`${kind === 'sheet' ? 'rs-sheet' : 'rs-dialog'} ${className ?? ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
        aria-labelledby={labelledBy}
        tabIndex={-1}
      >
        {kind === 'sheet' ? <div className="rs-sheet__grip" /> : null}
        {title || !hideClose ? (
          <div className="rs-panel__head">
            <h2 className="rs-panel__title">{title}</h2>
            {hideClose ? null : <IconButton icon={<CloseIcon />} label="Close" onClick={onClose} />}
          </div>
        ) : null}
        <div className="rs-panel__body">{children}</div>
        {footer ? <div className="rs-panel__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  )
}
